import {FC, useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useMatrixContext} from '@/contexts/matrix/client';

import {useMatrixRoomContext} from '@/contexts/matrix/room';
import TextMessageInputView from '@/components/matrix-chat/ui/text-message-input-view';
import {MultimediaUploadInfo} from '@/types/matrix';

const TextMessageInput: FC = () => {
  const {t} = useTranslation();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [text, setText] = useState<string>('');

  const {matrixClient} = useMatrixContext();
  const {
    matrixEventToQuote,
    currentMatrixRoomId,
    handleSendMatrixTextMessage,
    multimediaFiles,
    isSendingEvent,
    handleSendMultimediaFiles,
    handleUploadMultimediaFiles,
    handleSelectMultimediaTypeUploaded
  } = useMatrixRoomContext();

  const handleInputChange = useCallback(
    (newText: string) => {
      if (matrixClient && currentMatrixRoomId) {
        setText(newText);

        // matrixClient.sendTyping(currentMatrixRoomId, true, 5000);
      }
    },
    [currentMatrixRoomId, matrixClient]
  );

  const handleSendClick = useCallback(async () => {
    if (isSendingEvent) return;

    try {
      if (multimediaFiles && multimediaFiles.length > 0) {
        await handleSendMultimediaFiles(multimediaFiles, text);

        setText('');
      } else if (text !== '') {
        await handleSendMatrixTextMessage(text);
        setText('');
      }
    } catch (error) {
      console.error('Failed to send the message', error);
    }
  }, [
    isSendingEvent,
    multimediaFiles,
    text,
    handleSendMultimediaFiles,
    handleSendMatrixTextMessage
  ]);

  const handleUploadMultimedia = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (
      event.target.files &&
      event.target.files.length > 0 &&
      event.target.files.length <= 10
    ) {
      const selectedFiles = Array.from(event.target.files);

      const firstFileMimeType = selectedFiles[0].type.split('/')[0];

      if (firstFileMimeType === 'image') {
        handleSelectMultimediaTypeUploaded('image');
      } else if (firstFileMimeType === 'video') {
        handleSelectMultimediaTypeUploaded('video');
      }

      const filesToUpload: MultimediaUploadInfo[] = [];

      selectedFiles.forEach((file) => {
        const fileSize = file.size / 1024 / 1024;
        if (fileSize > 50) {
          return;
        }

        const fileMimeType = file.type.split('/')[0];
        if (fileMimeType === 'image' || fileMimeType === 'video') {
          filesToUpload.push({type: fileMimeType, file: file});
        }
      });

      if (filesToUpload) {
        handleUploadMultimediaFiles(filesToUpload);
      }
    } else if (event.target.files && event.target.files.length > 10) {
      const maxFiles = 10;
      alert(t('max_file_upload_limit', {count: maxFiles}));
      event.target.files = null;
    }
  };

  // useEffect(() => {
  //   const handleTyping = (event: MatrixEvent, member: RoomMember) => {
  //     if (event.getRoomId() !== roomId) {
  //       return;
  //     }
  //     console.log('Typing event detected:', event);
  //     if (member.typing) {
  //       console.log(member.name + ' is typing...');
  //     } else {
  //       console.log(member.name + ' stopped typing.');
  //     }
  //   };

  //   matrixClient?.on(RoomMemberEvent.Typing, handleTyping);

  //   return () => {
  //     // Cleanup the event listener on component unmount
  //     matrixClient?.removeListener(RoomMemberEvent.Typing, handleTyping);
  //   };
  // }, [matrixClient]);

  // This hook is making us some trouble. Should be fixed

  // useEffect(() => {
  //   async function loadInitialMessages() {
  //     if (matrixClient) {
  //       const room = matrixClient.getRoom(roomId);
  //       if (room) {
  //         const liveTimeline = room.getLiveTimeline();

  //         // Use scrollback to load previous messages
  //         await matrixClient.scrollback(room, 10);

  //         const events = liveTimeline.getEvents();
  //         const messages = events
  //           .map((event) => event.event)
  //           .filter((event) => event.type === 'm.room.message');

  //         console.log('Room Messages: ', messages);
  //         //dispatch(setMatrixEvents(messages));
  //       }
  //     }
  //   }

  //   loadInitialMessages();
  // }, [matrixClient, roomId]);

  useEffect(() => {
    if (matrixEventToQuote && inputRef.current) {
      inputRef.current.focus();
    }
  }, [matrixEventToQuote]);

  useEffect(() => {
    if (!multimediaFiles && inputRef.current) {
      inputRef.current.value = '';
    }
  }, [multimediaFiles]);

  return (
    <TextMessageInputView
      inputRef={inputRef}
      text={text}
      onTextChange={handleInputChange}
      onSendClick={handleSendClick}
      sendTooltipLabel={t('send')}
      multimediaFiles={multimediaFiles}
      onMultimediaUpload={handleUploadMultimedia}
      sendImageTooltipLabel={t('upload_media')}
    />
  );
};

export default TextMessageInput;
